import React, { useState, useMemo } from "react";
import projectsData from "../../Components/Data/projects.json";

// Color palette for category badges (bg + text)
const colorPalette = [
  ["bg-green-100", "text-green-800"],
  ["bg-blue-100", "text-blue-800"],
  ["bg-yellow-100", "text-yellow-800"],
  ["bg-purple-100", "text-purple-800"],
  ["bg-red-100", "text-red-800"],
  ["bg-pink-100", "text-pink-800"],
  ["bg-indigo-100", "text-indigo-800"],
  ["bg-teal-100", "text-teal-800"],
];

export default function ProjectsPage() {
  const [filters, setFilters] = useState({
    Project: "",
    Organization: "",
    Date: "",
    Category: "",
  });

  // Extract unique categories from all projects, flattening arrays if needed
  const uniqueCategories = useMemo(() => {
    const categoriesSet = new Set();
    projectsData.forEach((p) => {
      if (Array.isArray(p.Category)) {
        p.Category.forEach((cat) => categoriesSet.add(cat));
      } else if (p.Category) {
        categoriesSet.add(p.Category);
      }
    });
    return [...categoriesSet];
  }, []);

  // Map categories to colors dynamically
  const categoryColors = useMemo(() => {
    const map = {};
    uniqueCategories.forEach((cat, idx) => {
      const [bg, text] = colorPalette[idx % colorPalette.length];
      map[cat] = { bg, text };
    });
    return map;
  }, [uniqueCategories]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // Filter projects with category filter supporting array or string
  const filteredProjects = projectsData.filter((project) => {
    const matchesCategory =
      filters.Category === "" ||
      (Array.isArray(project.Category)
        ? project.Category.includes(filters.Category)
        : project.Category === filters.Category);

    return (
      (filters.Project === "" ||
        project.Project.toLowerCase().includes(
          filters.Project.toLowerCase()
        )) &&
      (filters.Organization === "" ||
        project.Organization === filters.Organization) &&
      (filters.Date === "" || project.Date === filters.Date) &&
      matchesCategory
    );
  });

  // Helper to get unique values for filters
  const uniqueValues = (field) => {
    const valuesSet = new Set();
    projectsData.forEach((p) => {
      if (field === "Category") {
        if (Array.isArray(p.Category)) {
          p.Category.forEach((cat) => valuesSet.add(cat));
        } else if (p.Category) {
          valuesSet.add(p.Category);
        }
      } else if (p[field]) {
        valuesSet.add(p[field]);
      }
    });
    return [...valuesSet];
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Heading */}
      <h1 className="text-4xl font-extrabold text-gray-900 text-center mb-6 tracking-wide relative">
        Completed Projects
        <span
          aria-hidden="true"
          className="block mx-auto mt-2 h-1 w-20 rounded-full bg-gradient-to-r from-green-400 to-green-600"
        />
      </h1>

      {/* Filters */}
      <div className="bg-blue-50 p-4 rounded-lg shadow-md border border-blue-100 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Project Name Search */}
          <input
            type="text"
            placeholder="Search Project Name"
            className="border border-blue-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            value={filters.Project}
            onChange={(e) => handleFilterChange("Project", e.target.value)}
          />

          {/* Organization Dropdown */}
          <select
            className="border border-blue-200 rounded-lg p-2 focus:ring-2 focus:ring-blue-400"
            value={filters.Organization}
            onChange={(e) => handleFilterChange("Organization", e.target.value)}
          >
            <option value="">All Organizations</option>
            {uniqueValues("Organization").map((org) => (
              <option key={org} value={org}>
                {org}
              </option>
            ))}
          </select>

          {/* Year Dropdown */}
          <select
            className="border border-blue-200 rounded-lg p-2 focus:ring-2 focus:ring-blue-400"
            value={filters.Date}
            onChange={(e) => handleFilterChange("Date", e.target.value)}
          >
            <option value="">All Years</option>
            {uniqueValues("Date").map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          {/* Category Dropdown */}
          <select
            className="border border-blue-200 rounded-lg p-2 focus:ring-2 focus:ring-blue-400"
            value={filters.Category}
            onChange={(e) => handleFilterChange("Category", e.target.value)}
          >
            <option value="">All Categories</option>
            {uniqueCategories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto shadow-lg rounded-lg border border-green-200">
        <table className="min-w-full divide-y divide-green-200">
          <thead className="bg-green-100">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-green-900 uppercase tracking-wider">
                Project Name
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-green-900 uppercase tracking-wider">
                Organization
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-green-900 uppercase tracking-wider">
                Year
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-green-900 uppercase tracking-wider">
                Category
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-amber-100">
            {filteredProjects.length > 0 ? (
              filteredProjects.map((project, index) => (
                <tr
                  key={index}
                  className={`transition duration-200 ease-in-out ${
                    index % 2 === 0 ? "bg-white" : "bg-amber-50/40"
                  } hover:bg-pink-50`}
                >
                  <td className="px-6 py-4">{project.Project}</td>
                  <td className="px-6 py-4">{project.Organization}</td>
                  <td className="px-6 py-4">{project.Date}</td>
                  <td className="px-6 py-4">
                    {Array.isArray(project.Category) ? (
                      project.Category.map((cat, idx) => {
                        const colors = categoryColors[cat] || {
                          bg: "bg-gray-200",
                          text: "text-gray-800",
                        };
                        return (
                          <span
                            key={idx}
                            className={`inline-block mr-1 mb-1 px-3 py-1 text-xs font-medium rounded-full ${colors.bg} ${colors.text}`}
                          >
                            {cat}
                          </span>
                        );
                      })
                    ) : (
                      <span
                        className={`px-3 py-1 text-xs font-medium rounded-full ${
                          (categoryColors[project.Category]?.bg ||
                            "bg-gray-200") +
                          " " +
                          (categoryColors[project.Category]?.text ||
                            "text-gray-800")
                        }`}
                      >
                        {project.Category}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center py-6 text-gray-500">
                  No matching projects found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
